import tvContent from "~/integrations/tv-content";
import { _fetchData } from "~/helpers/store";
import { calculatePointPackagesToRemoveOrAdd } from "~/utils/tvAndInternet/tv/points";
import { entertainmentTracking } from "~/utils/tvAndInternet/tv/tracking";

const getDefaultState = () => {
	return {
		promise: null,
		data: [],
		error: null,
		toAddUnsavedChannelIds: [],
		toRemoveUnsavedChannelIds: [],
		isAddDefaultSetup: false,
		addonModal: {
			show: false,
			removingChannelWithDependentAddons: false,
			dependentAddonsLocked: false,
			triggeringChannel: null,
			addonOptions: [],
			text: "",
		},
		isSwitchingMutuallExclusive: false,
	};
};

export const state = () => getDefaultState();
export const actions = {
	async fetchData(context, { subscriptionId, force, isTweBoxWebApp = false }) {
		if (subscriptionId && !isTweBoxWebApp) {
			return _fetchData(() => tvContent.getSubscriptionTvContent(subscriptionId), {
				context,
				force,
				name: "tvContentSubscription",
			});
		} else if (isTweBoxWebApp) {
			return _fetchData(() => tvContent.getSubscriptionTvContentStb(), {
				context,
				force,
				name: "tvContentSubscription",
			});
		}
	},
	async validateSelectedContent(context, { subscriptionId, channelIds }) {
		if (subscriptionId) {
			return tvContent.validateSelectedContent(subscriptionId, channelIds);
		} else {
			return tvContent.validateSelectedContentStb(channelIds);
		}
	},
	async updateSelectedContent(context, { subscriptionId, channelIds, smsCode }) {
		if (subscriptionId) {
			return tvContent.updateSelectedContent(subscriptionId, channelIds);
		} else {
			return tvContent.updateSelectedContentStb(channelIds, smsCode);
		}
	},
	async resetToDefaultSetup(context, { subscriptionId }) {
		return tvContent.resetToDefaultSetup(subscriptionId);
	},
	async validateDefaultSetup(context, { subscriptionId }) {
		return tvContent.validateDefaultSetup(subscriptionId);
	},
	async getNotifications(context, { subscriptionId }) {
		return tvContent.getNotifications(subscriptionId);
	},
	async getSubscriptionRentedMovies(context, { subscriptionId }) {
		return tvContent.getSubscriptionRentedMovies(subscriptionId);
	},
	addRemoveChannel(context, { channelId }) {
		const channelInfo = context.rootGetters["mittTelenor/fixed/tvContent/getChannelById"](channelId);
		const providerId = channelInfo?.thirdPartyInfo?.providerId;
		const getChannelsFromProvider = (providerId) =>
			context.rootGetters["mittTelenor/fixed/tvContent/getChannelsByProviderId"](providerId);
		const providerChannels = providerId ? getChannelsFromProvider(providerId) : [];
		const providerAddons = providerChannels.filter((channel) => channel?.requiresAtLeastOne);
		const providerHasAddons = providerAddons?.length > 0;
		const isAddonChannel = Array.isArray(channelInfo?.requiresAtLeastOne) && channelInfo?.requiresAtLeastOne.length > 0;

		const isChannelAdded = (checkChannelId) => {
			if (context.getters.toRemoveUnsavedChannelIds?.includes(checkChannelId)) {
				return false;
			}
			if (
				context.getters.toAddUnsavedChannelIds?.includes(checkChannelId) ||
				context.getters.isChannelInSubscription(checkChannelId)
			) {
				return true;
			}
			return false;
		};

		const handledAddonChannelAdded = () => {
			if (isAddonChannel) {
				if (addonIsMeetingRequirements(channelId)) return true;
				const addonOptions = providerChannels.filter((channel) => channelInfo?.requiresAtLeastOne.includes(channel.id));
				context.commit("setAddonModal", {
					show: true,
					removingChannelWithDependentAddons: false,
					dependentAddonsLocked: false,
					triggeringChannel: channelInfo,
					addonOptions,
					text: formatAddonTextAdding(addonOptions),
				});
				return false;
			}
			return true;
		};

		const addonIsMeetingRequirements = (addonId) => {
			const addonToCheck = providerAddons.find((channel) => channel.id === addonId);
			if (!addonToCheck?.requiresAtLeastOne) return true;
			return addonToCheck.requiresAtLeastOne.some((requiredChannelId) => isChannelAdded(requiredChannelId));
		};

		const formatAddonTextAdding = (addonOptions) => {
			if (!Array.isArray(addonOptions) || addonOptions.length === 0) return "";
			const formattedOptions =
				addonOptions.length <= 1
					? addonOptions.map((channel) => channel.name).join("")
					: addonOptions
							.slice(0, -1)
							.map((channel) => channel.name)
							.join(", ") +
						" eller " +
						addonOptions.slice(-1).map((channel) => channel.name);
			return `Hvis du ønsker <strong>${channelInfo.name}</strong>, må du legge til ${formattedOptions}.`;
		};

		const handledChannelToBeRemovedHasAddonChannel = () => {
			if (context.state.isSwitchingMutuallExclusive) return true;
			if (!isAddonChannel && providerHasAddons) {
				const addonChannelsThatWillLoseRequirement = providerAddons.filter((addonChannel) => {
					return (
						addonChannel.requiresAtLeastOne.some((requiredChannelId) => requiredChannelId === channelId) &&
						isChannelAdded(addonChannel.id)
					);
				});

				if (addonChannelsThatWillLoseRequirement.length === 0) return true;

				const lockedAddons = addonChannelsThatWillLoseRequirement.filter((channel) =>
					context.getters.isChannelLocked(channel.id),
				);

				if (lockedAddons.length > 0) {
					context.commit("setAddonModal", {
						show: true,
						removingChannelWithDependentAddons: true,
						dependentAddonsLocked: true,
						triggeringChannel: channelInfo,
						addonOptions: lockedAddons,
						text: formatAddonIsLockedText(lockedAddons),
					});
					return false;
				}

				context.commit("setAddonModal", {
					show: true,
					removingChannelWithDependentAddons: true,
					dependentAddonsLocked: false,
					triggeringChannel: channelInfo,
					addonOptions: addonChannelsThatWillLoseRequirement,
					text: formatRemoveDependentAddonsText(addonChannelsThatWillLoseRequirement),
				});
				return false;
			}
			return true;
		};

		const formatAddonIsLockedText = (lockedAddons) => {
			if (!Array.isArray(lockedAddons) || lockedAddons.length === 0) return "";
			const formattedLockedAddons = lockedAddons
				.map((channel) => {
					return `${context.getters.lockedDaysRemaining(channel.id)} dager igjen på <strong>${channel.name}</strong>`;
				})
				.join(", ");
			return `Du kan ikke fjerne <strong>${channelInfo.name}</strong> fordi du har en låst periode på ${formattedLockedAddons}`;
		};

		const formatRemoveDependentAddonsText = (addonChannels) => {
			if (!Array.isArray(addonChannels) || addonChannels.length === 0) return "";
			const formattedDependentPlans = addonChannels.map((channel) => channel.name).join(", ");
			return `<strong>${channelInfo.name}</strong> er tilknyttet <strong>${formattedDependentPlans}</strong>. For å kunne fjerne <strong>${channelInfo.name}</strong> må du derfor også fjerne <strong>${formattedDependentPlans}</strong>`;
		};

		if (isChannelAdded(channelId) && context.getters.toAddUnsavedChannelIds.includes(channelId)) {
			if (!handledChannelToBeRemovedHasAddonChannel()) return;
			context.commit("toAddUnsavedChannelIdsRemove", channelId);
			context.dispatch("trackingChannelSelection", { channelId, quantity: 1, remove: true });

			return;
		}
		if (isChannelAdded(channelId) && !context.getters.toAddUnsavedChannelIds.includes(channelId)) {
			if (!handledChannelToBeRemovedHasAddonChannel()) return;
			context.commit("toRemoveUnsavedChannelIdsPush", channelId);
			context.dispatch("trackingChannelSelection", { channelId, quantity: -1 });

			return;
		}
		if (!isChannelAdded(channelId) && context.getters.toRemoveUnsavedChannelIds.includes(channelId)) {
			if (!handledChannelToBeRemovedHasAddonChannel()) return;
			context.commit("toRemoveUnsavedChannelIdsRemove", channelId);
			context.dispatch("trackingChannelSelection", { channelId, quantity: -1, remove: true });

			return;
		}
		if (!isChannelAdded(channelId) && !context.getters.toRemoveUnsavedChannelIds.includes(channelId)) {
			if (!handledAddonChannelAdded()) return;
			context.commit("toAddUnsavedChannelIdsPush", channelId);
			context.dispatch("trackingChannelSelection", { channelId, quantity: 1 });
		}
	},

	trackingChannelSelection(context, { channelId, quantity, remove = false }) {
		const productInfo = context.rootGetters["mittTelenor/fixed/tvContent/getChannelById"](channelId);
		if (!productInfo) return;
		const channelProductInfo = {
			productArea: "Entertainment",
			categoryName: "Channels and streaming",
			subCategoryName: productInfo.type,
			id: channelId,
			name: productInfo.name,
			price: productInfo.points,
			quantity,
			details: {
				paymentMethod: "Subscription",
			},
		};
		const pointPackages = context.getters.unsavedExtraPointPackages;
		entertainmentTracking.channelChange(channelProductInfo, pointPackages, remove);
	},
};
export const mutations = {
	setData(state, data) {
		state.data = data;
	},
	setPromise(state, promise) {
		state.promise = promise;
	},
	setError(state, error) {
		state.error = error;
	},
	setIsAddDefaultSetup(state, value) {
		state.isAddDefaultSetup = value;
	},
	resetState(state, includeServerData) {
		if (includeServerData) {
			Object.assign(state, getDefaultState());
		} else {
			state.toAddUnsavedChannelIds = [];
			state.toRemoveUnsavedChannelIds = [];
			state.subscriptionId = "";
			state.isAddDefaultSetup = false;
		}
	},
	resetToAddAndRemoveUnsavedChannelIds(state) {
		state.toAddUnsavedChannelIds = [];
		state.toRemoveUnsavedChannelIds = [];
	},
	toAddUnsavedChannelIdsPush(state, channelId) {
		state.toAddUnsavedChannelIds.push(channelId);
	},
	toAddUnsavedChannelIdsRemove(state, channelId) {
		const indexToRemove = state.toAddUnsavedChannelIds.indexOf(channelId);
		if (indexToRemove > -1) {
			state.toAddUnsavedChannelIds.splice(indexToRemove, 1);
		}
	},
	toRemoveUnsavedChannelIdsPush(state, channelId) {
		state.toRemoveUnsavedChannelIds.push(channelId);
	},
	toRemoveUnsavedChannelIdsRemove(state, channelId) {
		const indexToRemove = state.toRemoveUnsavedChannelIds.indexOf(channelId);
		if (indexToRemove > -1) {
			state.toRemoveUnsavedChannelIds.splice(indexToRemove, 1);
		}
	},
	setAddonModal(state, value) {
		state.addonModal = {
			...state.addonModal,
			...value,
		};
	},
	resetAddonModal(state) {
		state.addonModal = {
			show: false,
			removingChannelWithDependentAddons: false,
			dependentAddonsLocked: false,
			triggeringChannel: null,
			addonOptions: [],
			text: "",
		};
	},
	setSwitchingMutualExclusive(state, value) {
		state.isSwitchingMutuallExclusive = value;
	},
};
export const getters = {
	loading: (state) => {
		return state.promise !== null;
	},
	error: (state) => {
		return state.error;
	},
	subscriptionTvContent: (state) => {
		return state.data;
	},
	toAddUnsavedChannelIds: (state) => {
		return state.toAddUnsavedChannelIds;
	},
	toRemoveUnsavedChannelIds: (state) => {
		return state.toRemoveUnsavedChannelIds;
	},
	isAddDefaultSetup: (state) => {
		return state.isAddDefaultSetup;
	},
	channelIdsSelectedByUser: (state, getters) => {
		let subscribedContent = [];
		try {
			subscribedContent = JSON.parse(JSON.stringify(getters.subscriptionTvContent.subscribedContent)) || [];
		} catch (e) {
			console.error("Error parsing subscribedContent", e);
		}
		getters.toAddUnsavedChannelIds.forEach((channelId) => {
			subscribedContent.push(channelId);
		});
		getters.toRemoveUnsavedChannelIds.forEach((channelId) => {
			const index = subscribedContent.indexOf(channelId);
			if (index > -1) {
				subscribedContent.splice(index, 1);
			}
		});
		return subscribedContent;
	},
	unsavedUsedPoints: (state, getters, rootState, rootGetters) => {
		const toAddUnsavedChannels = rootGetters["mittTelenor/fixed/tvContent/getChannelsByIds"](
			getters.toAddUnsavedChannelIds,
		);
		let totalPointsFromToAddUnsavedChannels = 0;
		toAddUnsavedChannels.forEach((channel) => {
			totalPointsFromToAddUnsavedChannels += channel.points;
		});

		const toRemoveUnsavedChannels = rootGetters["mittTelenor/fixed/tvContent/getChannelsByIds"](
			getters.toRemoveUnsavedChannelIds,
		);
		let totalPointsFromToRemoveUnsavedChannels = 0;
		toRemoveUnsavedChannels.forEach((channel) => {
			totalPointsFromToRemoveUnsavedChannels += channel.points;
		});

		const getSavedUnsavedBBSpeedPointDiff = () => {
			const unsavedSelectedFrihetBBSpeedProduct =
				rootGetters["mittTelenor/fixed/frihetSubscription/unsavedSelectedFrihetBBSpeedProduct"];
			const savedSelectedFrihetBBSpeedProduct =
				rootGetters["mittTelenor/fixed/frihetSubscription/savedSelectedFrihetBBSpeedProduct"];
			const pointsFromUnsavedSelectedFrihetBBSpeed = unsavedSelectedFrihetBBSpeedProduct?.prices?.POINTS?.amount || 0;
			const pointsFromSavedSelectedFrihetBBSpeed = savedSelectedFrihetBBSpeedProduct?.prices?.POINTS?.amount || 0;
			let savedUnsavedBBSpeedDifference = 0;
			if (unsavedSelectedFrihetBBSpeedProduct) {
				savedUnsavedBBSpeedDifference = -pointsFromSavedSelectedFrihetBBSpeed;
				savedUnsavedBBSpeedDifference += pointsFromUnsavedSelectedFrihetBBSpeed;
			}
			return savedUnsavedBBSpeedDifference;
		};
		const getSavedUnsavedTvPointDiff = () => {
			const unsavedSelectedFrihetTvProduct =
				rootGetters["mittTelenor/fixed/frihetSubscription/unsavedSelectedFrihetTvProduct"];
			const savedSelectedFrihetTvProduct =
				rootGetters["mittTelenor/fixed/frihetSubscription/savedSelectedFrihetTvProduct"];
			const unsavedSelectedTvProductPoints = unsavedSelectedFrihetTvProduct?.prices?.POINTS?.amount || 0;
			const savedSelectedTvProductPoints = savedSelectedFrihetTvProduct?.prices?.POINTS?.amount || 0;
			let savedUnsavedPointsDiff = 0;
			if (unsavedSelectedFrihetTvProduct) {
				savedUnsavedPointsDiff = -savedSelectedTvProductPoints;
				savedUnsavedPointsDiff += unsavedSelectedTvProductPoints;
			}
			const disableTvProduct = rootGetters["mittTelenor/fixed/frihetSubscription/disableTvProduct"];
			if (unsavedSelectedFrihetTvProduct && unsavedSelectedFrihetTvProduct?.productId === disableTvProduct?.productId) {
				savedUnsavedPointsDiff -= getters.subscribedContentTotalPointValue;
			}
			return savedUnsavedPointsDiff;
		};
		const getCostDefaultSetup = getters.isAddDefaultSetup ? getters.costDefaultSetup : 0;

		return (
			totalPointsFromToAddUnsavedChannels -
			totalPointsFromToRemoveUnsavedChannels +
			getSavedUnsavedBBSpeedPointDiff() +
			getSavedUnsavedTvPointDiff() +
			getCostDefaultSetup
		);
	},
	subscribedContentTotalPointValue: (state, getters, rootState, rootGetters) => {
		let total = 0;
		const subscribedChannels = rootGetters["mittTelenor/fixed/tvContent/getChannelsByIds"](
			getters.subscriptionTvContent?.subscribedContent,
		);
		subscribedChannels?.forEach((channel) => {
			total += channel?.points || 0;
		});
		return total;
	},
	unsavedExtraPointPackages: (state, getters) => {
		// How many point packages should the user have after the selection they have made
		const targetPointPackages = Math.ceil(getters.totalUsedPoints / getters.pointsInPackage) || 0;
		const includedPointPackages = Math.ceil(getters.includedPoints / getters.pointsInPackage) || 0;
		const freePointsPackages = getters.numberOfFreePointPackages;
		const boughtPointPackages = Math.ceil(getters.boughtPoints / getters.pointsInPackage) || 0;

		return calculatePointPackagesToRemoveOrAdd({
			includedPointPackages,
			freePointsPackages,
			targetPointPackages,
			boughtPointPackages,
		});
	},
	includedPoints: (state, getters) => {
		return getters.subscriptionTvContent?.pointsSummary?.included || 0;
	},
	freePoints: (state, getters) => {
		return getters.subscriptionTvContent?.pointsSummary?.free || 0;
	},
	numberOfFreePointPackages: (state, getters) => {
		return getters.subscriptionTvContent?.numberOfFreePointPackages || 0;
	},
	boughtPoints: (state, getters) => {
		return getters.subscriptionTvContent?.pointsSummary?.bought || 0;
	},
	totalUsedPoints: (state, getters) => {
		return getters.subscriptionTvContent?.pointsSummary?.used + getters.unsavedUsedPoints || 0;
	},
	extraPointsUsed: (state, getters) => {
		if (getters.totalUsedPoints > getters.includedPoints) {
			return getters.totalUsedPoints - getters.includedPoints;
		}
		return 0;
	},
	pointsInPackage: (state, getters) => {
		return getters.subscriptionTvContent?.extraPointPackage?.points || 0;
	},
	costDefaultSetup: (state, getters) => {
		return getters.subscriptionTvContent?.costDefaultSetup || 0;
	},
	pointsOverIncludedAndBought: (state, getters) => {
		const pointsOverIncludedAndBought = getters.extraPointsUsed - getters.boughtPoints;
		return pointsOverIncludedAndBought;
	},
	pointsUnderIncludedAndBought: (state, getters) => {
		const allPoints = getters.includedPoints + getters.boughtPoints;
		const pointsUnderIncludedAndBought = allPoints - getters.totalUsedPoints;
		return pointsUnderIncludedAndBought > 0 ? pointsUnderIncludedAndBought : 0;
	},
	totalPointsFromPackages: (state, getters) => {
		let pointsFromUnsavedExtraPointPackages = 0;
		if (getters.unsavedExtraPointPackages > 0) {
			pointsFromUnsavedExtraPointPackages = getters.unsavedExtraPointPackages * getters.pointsInPackage;
		}

		return getters.boughtPoints + pointsFromUnsavedExtraPointPackages || 0;
	},
	includedPointsUsed: (state, getters) => {
		if (getters.totalUsedPoints > getters.includedPoints) {
			return getters.includedPoints;
		}
		return getters.totalUsedPoints;
	},
	pointPackageCostNok: (state, getters) => {
		if (getters.subscriptionTvContent?.extraPointPackage?.price?.currency === "NOK") {
			return getters.subscriptionTvContent?.extraPointPackage?.price?.amount;
		}
		return 0;
	},
	isChannelInSubscription: (state, getters) => (channelId) => {
		return getters.subscriptionTvContent.subscribedContent?.some((id) => id === channelId);
	},
	isChannelSelected: (state, getters) => (channelId) => {
		return (
			getters.toAddUnsavedChannelIds.includes(channelId) ||
			(getters.isChannelInSubscription(channelId) && !getters.toRemoveUnsavedChannelIds.includes(channelId))
		);
	},
	getSelectedMutualExclusiveChannelByMutualExclusiveGroup:
		(state, getters, rootState, rootGetters) => (mutuallyExclusiveGroup) => {
			const mutualExclusiveChannels =
				rootGetters["mittTelenor/fixed/tvContent/getChannelsByMutualExclusiveGroup"](mutuallyExclusiveGroup);
			return mutualExclusiveChannels.find((channel) => getters.isChannelSelected(channel.id));
		},
	isChannelChangeNotSaved: (state, getters) => (channelId) => {
		return getters.toAddUnsavedChannelIds.includes(channelId) || getters.toRemoveUnsavedChannelIds.includes(channelId);
	},
	isChannelLocked: (state, getters) => (channelId) => {
		const hasLockedDays = getters.lockedDaysRemaining(channelId) || 0;
		return hasLockedDays > 0;
	},
	lockedDaysRemaining: (state, getters) => (channelId) => {
		return getters.subscriptionTvContent?.lockedContent?.find((channel) => channel.id === channelId)?.remainingDays;
	},
	mutualExclusiveLockedDaysRemaining: (state, getters) => (mutuallyExclusiveGroup) => {
		const selectedMutualChannel =
			getters.getSelectedMutualExclusiveChannelByMutualExclusiveGroup(mutuallyExclusiveGroup);
		return getters.lockedDaysRemaining(selectedMutualChannel?.id);
	},
	processSelectedContentValidationResult: (state, getters) => (validationResult) => {
		const packagesToRemove = getters.unsavedExtraPointPackages < 0 ? Math.abs(getters.unsavedExtraPointPackages) : 0;
		const packagesToAdd = getters.unsavedExtraPointPackages > 0 ? Math.abs(getters.unsavedExtraPointPackages) : 0;

		const missMatchClientServerPackageAmount =
			validationResult.successful &&
			(packagesToRemove !== validationResult.numberOfPointPackagesToRemove ||
				packagesToAdd !== validationResult.numberOfPointPackagesToAdd);

		const error = { message: "", code: "" };
		if (validationResult?.errors?.length) {
			validationResult.errors.forEach((err) => {
				error.message += err.message + "</br>";
				error.code += err.code + "</br>";
			});
		}
		if (missMatchClientServerPackageAmount) {
			error.message += "Poengpakke antall i klient stemmer ikke med respons fra validerings API.";
			console.error(
				`Poengpakke antall i klient stemmer ikke med respons fra validerings API. 
				Client packagesToAdd: ${packagesToAdd} Server packagesToAdd: ${validationResult.numberOfPointPackagesToAdd} 
				Client packagesToRemove: ${packagesToRemove} Server packagesToRemove: ${validationResult.numberOfPointPackagesToRemove}`,
			);
		}
		if (!validationResult.successful || missMatchClientServerPackageAmount || validationResult?.errors?.length) {
			return { error };
		}
	},
	getSubscribedThirdpartyChannels: (state, getters, rootState, rootGetters) => {
		return rootGetters["mittTelenor/fixed/tvContent/getChannelsByIds"](
			getters.subscriptionTvContent?.subscribedContent,
		)?.filter((channel) => channel.thirdPartyInfo);
	},
	providerInConflict: (state, getters) => {
		return getters.subscriptionTvContent?.providerInConflict;
	},
};
